import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { requestPartnership } from "../../../api/partnership";
import { Button } from "../../common/Button";
import { FormInput } from "../../common/FormLayout";
import { Modal, ModalActions } from "../../common/Modal";

type RequestPartnershipModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Libellé de ce qu'on cherche ("un installateur", "un éleveur") — dépend du type de compte. */
  targetLabel: string;
};

/**
 * Demande de partenariat : l'utilisateur saisit UN email, rien d'autre.
 *
 * ⚠️ Le backend répond toujours la même chose (202 + message neutre), que l'email existe ou
 * non et quel que soit le type de la personne visée. C'est volontaire : sans ça, on pourrait
 * énumérer des adresses et découvrir qui est client de Copernic.
 *
 * Cet écran doit donc afficher le message du serveur TEL QUEL, et ne jamais chercher à
 * indiquer si la demande a réellement abouti.
 */
export const RequestPartnershipModal = ({
  isOpen,
  onClose,
  targetLabel,
}: RequestPartnershipModalProps) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setEmail("");
    setServerMessage(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Veuillez saisir un email");
      return;
    }

    setSubmitting(true);
    setError(null);
    const result = await requestPartnership(trimmed);
    setSubmitting(false);

    if (!result.success) {
      setError(result.error);
      return;
    }
    setServerMessage(result.message);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Demander un partenariat"
      icon="handshake"
      maxWidth="md"
    >
      {serverMessage ? (
        // Accusé de réception volontairement neutre — voir le commentaire d'en-tête.
        <div className="text-center py-4">
          <div className="mb-4 text-primary">
            <FontAwesomeIcon icon="paper-plane" size="3x" />
          </div>
          <p className="text-lg mb-6">{serverMessage}</p>
          <ModalActions>
            <Button onClick={close}>Fermer</Button>
          </ModalActions>
        </div>
      ) : (
        <div className="py-2">
          <p className="text-tertiary mb-4">
            Saisissez l'adresse email de {targetLabel}. Il recevra une demande
            qu'il pourra accepter ou refuser.
          </p>

          <FormInput
            label="Email"
            type="email"
            required
            value={email}
            error={error ?? undefined}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !submitting) handleSubmit();
            }}
            placeholder="prenom.nom@exemple.fr"
          />

          <ModalActions>
            <Button onClick={close} variant="secondary">
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Envoi…" : "Envoyer la demande"}
            </Button>
          </ModalActions>
        </div>
      )}
    </Modal>
  );
};
